import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApplication, usePublicConfig } from '../context';
import type { ApplicationStatusResponse } from '../services';

export const ApplicationSuccessPage = () => {
  const navigate = useNavigate();
  const { lastSubmission, lastSubmittedPhone, checkStatus, clearError, error } = useApplication();
  const { config: publicConfig } = usePublicConfig();
  const chamaName = publicConfig?.name ?? 'LoopFund';

  const [statusPhone, setStatusPhone] = useState(() => lastSubmittedPhone ?? '');
  const [statusResult, setStatusResult] = useState<ApplicationStatusResponse | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    if (!lastSubmission) {
      navigate('/apply', { replace: true });
    }
  }, [lastSubmission, navigate]);

  useEffect(() => {
    if (lastSubmittedPhone) {
      setStatusPhone(lastSubmittedPhone);
    }
  }, [lastSubmittedPhone]);

  const submissionSummary = useMemo(() => {
    if (!lastSubmission) {
      return null;
    }

    return [
      { label: 'Application ID', value: `#${lastSubmission.user_id}` },
      { label: 'Current status', value: lastSubmission.status.replace(/_/g, ' ') },
      { label: 'Submission note', value: lastSubmission.message },
      ...(lastSubmission.note ? [{ label: 'Next steps', value: lastSubmission.note }] : []),
    ];
  }, [lastSubmission]);

  const handleStatusCheck = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatusError(null);
    setStatusResult(null);

    const trimmedPhone = statusPhone.trim();

    if (!trimmedPhone) {
      setStatusError('Enter the phone number you used for the application.');
      return;
    }

    setCheckingStatus(true);
    clearError();

    try {
      const result = await checkStatus(trimmedPhone);
      setStatusResult(result);
    } catch (statusErr) {
      const message = statusErr instanceof Error ? statusErr.message : 'Unable to retrieve status. Please try again.';
      setStatusError(message);
    } finally {
      setCheckingStatus(false);
    }
  };

  if (!lastSubmission) {
    return null;
    }

  return (
    <section className="relative flex min-h-screen w-full flex-col bg-slate-900 text-slate-100 lg:flex-row">
      <div
        className="fixed inset-x-0 top-0 h-[48vh] w-full overflow-hidden lg:hidden"
        style={{ backgroundImage: "url('https://i.ibb.co/7xFJnX3B/139539.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-6 pb-[calc(12vh+env(safe-area-inset-bottom))] pt-16">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-brand-100">
            Application received
          </span>
          <h1 className="mt-3 font-display text-3xl leading-snug text-white">We got your application.</h1>
          <p className="mt-2 text-sm leading-relaxed text-brand-100/80">
            Expect a decision within seven days. We will reach out via SMS with next steps.
          </p>
        </div>
      </div>

      <div
        className="relative hidden flex-1 items-center justify-center px-16 py-20 lg:flex"
        style={{ backgroundImage: "url('https://i.ibb.co/7xFJnX3B/139539.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative z-10 max-w-md space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-brand-100">
            Application received
          </span>
          <h1 className="font-display text-4xl leading-tight text-white">
            Thank you for applying to {chamaName}.
          </h1>
          <p className="text-base leading-relaxed text-brand-100/70">
            We review new member applications every week. Expect an SMS with the outcome and onboarding instructions within
            seven days.
          </p>
          <ul className="space-y-3 text-sm text-brand-100/80">
            <li className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3">
              <p className="font-semibold text-white">Screening</p>
              <p className="mt-1">The vetting committee verifies your information and motivation.</p>
            </li>
            <li className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3">
              <p className="font-semibold text-white">Follow up</p>
              <p className="mt-1">If anything is unclear, we will reach out for supporting documentation.</p>
            </li>
            <li className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3">
              <p className="font-semibold text-white">Onboarding</p>
              <p className="mt-1">Approved applicants receive login credentials and orientation schedule via SMS.</p>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-[48vh] flex flex-1 items-end justify-center bg-transparent pb-8 lg:mt-0 lg:min-h-screen lg:items-center lg:bg-white lg:px-8 lg:py-16 lg:text-slate-900 dark:lg:bg-slate-950 dark:lg:text-slate-100">
        <div className="fixed inset-x-0 bottom-0 z-10 w-full rounded-t-[2.5rem] bg-white px-6 py-10 text-slate-900 shadow-2xl dark:bg-slate-950 dark:text-slate-100 max-h-[80vh] overflow-y-auto pb-[calc(16px+env(safe-area-inset-bottom))] lg:static lg:max-w-md lg:rounded-none lg:px-10 lg:py-16 lg:shadow-none lg:max-h-none lg:overflow-visible">
          <div>
            <h2 className="font-display text-3xl text-slate-900 dark:text-white">Next steps</h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">
              Keep this reference handy. You can check your application status anytime using your phone number.
            </p>
          </div>

          {submissionSummary ? (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
              <p className="font-semibold text-slate-900 dark:text-white">Application reference</p>
              <dl className="mt-4 space-y-3">
                {submissionSummary.map(({ label, value }) => (
                  <div key={label} className="flex flex-col gap-1">
                    <dt className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</dt>
                    <dd className="text-sm font-medium text-slate-800 dark:text-slate-100">{value}</dd>
                  </div>
                ))}
                {lastSubmittedPhone ? (
                  <div className="flex flex-col gap-1">
                    <dt className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">Submitted phone</dt>
                    <dd className="text-sm font-medium text-slate-800 dark:text-slate-100">{lastSubmittedPhone}</dd>
                  </div>
                ) : null}
              </dl>
            </div>
          ) : null}

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white/80 p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900/60">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Check application status</h3>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Enter your phone number and we will fetch the current status from {chamaName}.
            </p>
            <form onSubmit={handleStatusCheck} className="mt-4 space-y-3" noValidate>
              <div>
                <label htmlFor="statusPhone" className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Phone number
                </label>
                <input
                  id="statusPhone"
                  type="tel"
                  value={statusPhone}
                  onChange={(event) => setStatusPhone(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                  placeholder="+2547XXXXXXXX"
                />
              </div>

              <button
                type="submit"
                disabled={checkingStatus}
                className="inline-flex w-full items-center justify-center rounded-full bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-400"
              >
                {checkingStatus ? 'Checking status…' : 'Check status'}
              </button>
            </form>

            {statusError || error ? (
              <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                {statusError ?? error}
              </p>
            ) : null}

            {statusResult ? (
              <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200">
                <p className="font-semibold">Status: {statusResult.status.replace(/_/g, ' ')}</p>
                <p className="mt-1 text-xs uppercase tracking-wide text-emerald-600/80 dark:text-emerald-200/70">Message</p>
                <p className="text-sm">{statusResult.message}</p>
                <p className="mt-2 text-xs text-emerald-600/80 dark:text-emerald-200/70">
                  Applied on {new Date(statusResult.date_applied).toLocaleString()}
                </p>
                {statusResult.date_joined ? (
                  <p className="mt-1 text-xs text-emerald-600/80 dark:text-emerald-200/70">
                    Joined on {new Date(statusResult.date_joined).toLocaleString()}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
            <p className="font-semibold text-slate-900 dark:text-white">Need to amend your submission?</p>
            <p className="mt-2">
              Email{' '}
              <a href={`mailto:hello@${chamaName.toLowerCase().replace(' ', '')}.com`} className="font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-300">
                hello@{chamaName.toLowerCase().replace(' ', '')}.com
              </a>{' '}
              or call +254 700 111 222.
            </p>
          </div>

          <div className="mt-6 space-y-2 text-sm font-semibold text-brand-600 dark:text-brand-300">
            <Link to="/apply" className="block underline decoration-brand-500/60 decoration-2 transition hover:text-brand-700">
              Submit another application
            </Link>
            <Link to="/" className="block underline decoration-brand-500/60 decoration-2 transition hover:text-brand-700">
              Return to home
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};